import * as echarts from 'echarts'
import debounce from 'lodash-es/debounce'
import { useEffect, useRef } from 'react'

import { ANIMATION_DURATION } from '../consts'
import type { BaseChartProps } from '../types'

export const useBaseChart = ({
  data,
  animation = true,
}: Pick<BaseChartProps, 'data' | 'isLoading' | 'animation' | 'appearance'>) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.EChartsType | null>(null)

  useEffect(() => {
    if (chartRef.current && !chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, undefined, {
        renderer: 'svg',
      })

      // Delay ResizeObserver setup until animation completes
      const setupResizeObserver = () => {
        const debouncedResize = debounce(() => {
          chartInstance.current?.resize()
        }, 0)

        const resizeObserver = new ResizeObserver(() => {
          debouncedResize()
        })

        resizeObserver.observe(chartRef.current!)

        return resizeObserver
      }

      let resizeObserver: ResizeObserver | null = null

      // If animation is enabled, wait for it to complete before setting up ResizeObserver
      if (animation) {
        // Wait for initial animation to complete
        const timer = setTimeout(() => {
          resizeObserver = setupResizeObserver()
        }, ANIMATION_DURATION)

        return () => {
          clearTimeout(timer)
          resizeObserver?.disconnect()
          chartInstance.current?.dispose()
          chartInstance.current = null
        }
      }
      // If no animation, set up ResizeObserver immediately
      resizeObserver = setupResizeObserver()

      return () => {
        resizeObserver?.disconnect()
        chartInstance.current?.dispose()
        chartInstance.current = null
      }
    }
  }, [animation, data.length])

  return {
    chartRef,
    chartInstance,
  }
}
